import type {
  DisplayRules,
  PositionId,
  SheetLevel,
  StatKey,
  StatLine,
  StatSheetColumn,
} from '@fsp/sports-core';

function n(line: StatLine, key: StatKey): number {
  return line[key] ?? 0;
}

function summarizeOffense(line: StatLine): string {
  const parts: string[] = [];
  if (n(line, 'pass_att') > 0) {
    parts.push(`${n(line, 'pass_cmp')}/${n(line, 'pass_att')}, ${n(line, 'pass_yd')} yd`);
    if (n(line, 'pass_td')) parts.push(`${n(line, 'pass_td')} TD`);
    if (n(line, 'pass_int')) parts.push(`${n(line, 'pass_int')} INT`);
  }
  if (n(line, 'rush_att') > 0) {
    parts.push(`${n(line, 'rush_att')} car, ${n(line, 'rush_yd')} yd`);
    if (n(line, 'rush_td')) parts.push(`${n(line, 'rush_td')} rush TD`);
  }
  if (n(line, 'rec_tgt') > 0 || n(line, 'rec') > 0) {
    parts.push(`${n(line, 'rec')} rec, ${n(line, 'rec_yd')} yd`);
    if (n(line, 'rec_td')) parts.push(`${n(line, 'rec_td')} rec TD`);
  }
  if (n(line, 'fum_lost')) parts.push(`${n(line, 'fum_lost')} fum`);
  return parts.length ? parts.join(', ') : 'No stats yet';
}

function summarizeKicker(line: StatLine): string {
  const made =
    n(line, 'fg_0_19') +
    n(line, 'fg_20_29') +
    n(line, 'fg_30_39') +
    n(line, 'fg_40_49') +
    n(line, 'fg_50p');
  const att = made + n(line, 'fg_miss');
  const xpAtt = n(line, 'xp_made') + n(line, 'xp_miss');
  if (att === 0 && xpAtt === 0) return 'No stats yet';
  const parts = [`${made}/${att} FG`];
  if (n(line, 'fg_50p')) parts.push(`${n(line, 'fg_50p')} from 50+`);
  parts.push(`${n(line, 'xp_made')}/${xpAtt} XP`);
  return parts.join(', ');
}

function summarizeDefense(line: StatLine): string {
  const parts: string[] = [];
  if ('dst_pts_allowed' in line) parts.push(`${n(line, 'dst_pts_allowed')} PA`);
  if (n(line, 'dst_sack')) parts.push(`${n(line, 'dst_sack')} sack`);
  if (n(line, 'dst_int')) parts.push(`${n(line, 'dst_int')} INT`);
  if (n(line, 'dst_fum_rec')) parts.push(`${n(line, 'dst_fum_rec')} FR`);
  if (n(line, 'dst_td') + n(line, 'dst_ret_td'))
    parts.push(`${n(line, 'dst_td') + n(line, 'dst_ret_td')} TD`);
  return parts.length ? parts.join(', ') : 'No stats yet';
}

/** A defensive player's own line (Q11): "6 tkl, 1 sack, 1 INT". */
function summarizeDefender(line: StatLine): string {
  const parts: string[] = [];
  const tackles = n(line, 'idp_tkl_solo') + n(line, 'idp_tkl_ast');
  if (tackles) parts.push(`${tackles} tkl`);
  if (n(line, 'idp_sack')) parts.push(`${n(line, 'idp_sack')} sack`);
  if (n(line, 'idp_int')) parts.push(`${n(line, 'idp_int')} INT`);
  if (n(line, 'idp_pass_def')) parts.push(`${n(line, 'idp_pass_def')} PD`);
  if (n(line, 'idp_ff')) parts.push(`${n(line, 'idp_ff')} FF`);
  if (n(line, 'idp_fum_rec')) parts.push(`${n(line, 'idp_fum_rec')} FR`);
  if (n(line, 'idp_td')) parts.push(`${n(line, 'idp_td')} TD`);
  return parts.length ? parts.join(', ') : 'No stats yet';
}

/** The defensive players' positions: a player with only these reads as a defender. */
const DEFENDERS = new Set(['DL', 'LB', 'DB']);

const CASUAL: Record<string, StatKey[]> = {
  offense: ['pass_yd', 'pass_td', 'rush_yd', 'rush_td', 'rec', 'rec_yd', 'rec_td'],
  special: ['fg_0_19', 'fg_20_29', 'fg_30_39', 'fg_40_49', 'fg_50p', 'fg_miss', 'xp_made'],
  defense: ['dst_sack', 'dst_int', 'dst_fum_rec', 'dst_td', 'dst_pts_allowed'],
  idp: ['idp_tkl_solo', 'idp_tkl_ast', 'idp_sack', 'idp_int', 'idp_pass_def'],
};

const PRO: Record<string, StatKey[]> = {
  offense: [
    ...CASUAL.offense!,
    'pass_att',
    'pass_cmp',
    'rush_att',
    'rec_tgt',
    'snap_pct',
    'target_share',
    'rz_touches',
    'air_yd',
  ],
  special: CASUAL.special!,
  defense: [...CASUAL.defense!, 'dst_ff', 'dst_safety', 'dst_blk', 'dst_yds_allowed'],
  idp: [...CASUAL.idp!, 'idp_tkl_loss', 'idp_qb_hit', 'idp_ff', 'idp_fum_rec', 'idp_td'],
};

/**
 * A player card's game log (B-218), in the order a box score reads: passing,
 * rushing, receiving, then the fumble; a kicker's field goals by distance and
 * his extra points; a defense's points and yards allowed, then its takeaways.
 * The volume numbers (attempts, carries, targets) are here beside the ones
 * that score, because they are what a manager reads a week by. A column no
 * week has a number in is left out by the read.
 */
const GAME_LOG: Record<string, StatKey[]> = {
  offense: [
    'pass_cmp',
    'pass_att',
    'pass_yd',
    'pass_td',
    'pass_int',
    'rush_att',
    'rush_yd',
    'rush_td',
    'rec_tgt',
    'rec',
    'rec_yd',
    'rec_td',
    'fum_lost',
  ],
  special: [
    'fg_0_19',
    'fg_20_29',
    'fg_30_39',
    'fg_40_49',
    'fg_50p',
    'fg_miss',
    'xp_made',
    'xp_miss',
  ],
  defense: [
    'dst_pts_allowed',
    'dst_yds_allowed',
    'dst_sack',
    'dst_int',
    'dst_fum_rec',
    'dst_td',
    'dst_safety',
  ],
  idp: [
    'idp_tkl_solo',
    'idp_tkl_ast',
    'idp_tkl_loss',
    'idp_sack',
    'idp_qb_hit',
    'idp_int',
    'idp_pass_def',
    'idp_ff',
    'idp_fum_rec',
    'idp_td',
  ],
};

// EACH POSITION'S OWN COLUMNS (DIRECTION U29, PLAN-2026-10-26-numbers-that-
// fit-the-position §9.2): what it earns its points by and the volume behind
// it, the few a casual player looks for at Simple, the rest at Standard, the
// rates and shares at Everything — the big sites' columns by position
// (Sleeper's list, ESPN's and Yahoo's stats views). In reading order; a level
// leaves out what is above it. No snap share or red-zone touches: the free
// source has neither.
const one = (key: StatKey, level: SheetLevel): StatSheetColumn => ({
  key,
  level,
  figure: { stat: key },
});
const FUM = one('fum_lost', 'standard');
const TGT_SHARE: StatSheetColumn = {
  key: 'tgt_share',
  label: 'Tgt%',
  name: 'His share of his club’s targets.',
  level: 'everything',
  figure: { share: 'rec_tgt' },
  decimals: 1,
};
const AIR = one('air_yd', 'everything');
const YPR: StatSheetColumn = {
  key: 'rec_ypr',
  label: 'Y/R',
  name: 'Receiving yards a catch.',
  level: 'everything',
  figure: { rate: ['rec_yd'], per: ['rec'] },
};
const CATCHING = [
  one('rec_tgt', 'standard'),
  one('rec', 'simple'),
  one('rec_yd', 'simple'),
  YPR,
  one('rec_td', 'simple'),
  TGT_SHARE,
  AIR,
];
const TACKLES: StatSheetColumn = {
  key: 'idp_tkl',
  label: 'Tkl',
  name: 'Tackles, solo and assisted.',
  level: 'simple',
  figure: { sum: ['idp_tkl_solo', 'idp_tkl_ast'] },
};
const SOLO_AST = [one('idp_tkl_solo', 'standard'), one('idp_tkl_ast', 'standard')];
const IDP_TD: StatSheetColumn = { ...one('idp_td', 'everything'), label: 'TD' };

const SHEETS: Record<string, readonly StatSheetColumn[]> = {
  QB: [
    one('pass_cmp', 'standard'),
    one('pass_att', 'standard'),
    {
      key: 'pass_pct',
      label: 'Cmp%',
      name: 'Completions of passes thrown.',
      level: 'everything',
      figure: { rate: ['pass_cmp'], per: ['pass_att'], percent: true },
    },
    one('pass_yd', 'simple'),
    one('pass_td', 'simple'),
    one('pass_int', 'simple'),
    {
      key: 'pass_ypa',
      label: 'Y/A',
      name: 'Passing yards a pass thrown.',
      level: 'everything',
      figure: { rate: ['pass_yd'], per: ['pass_att'] },
    },
    one('rush_att', 'standard'),
    one('rush_yd', 'simple'),
    one('rush_td', 'simple'),
    FUM,
  ],
  RB: [
    one('rush_att', 'standard'),
    one('rush_yd', 'simple'),
    {
      key: 'rush_ypc',
      label: 'YPC',
      name: 'Rushing yards a carry.',
      level: 'everything',
      figure: { rate: ['rush_yd'], per: ['rush_att'] },
    },
    one('rush_td', 'simple'),
    one('rec_tgt', 'standard'),
    one('rec', 'simple'),
    one('rec_yd', 'simple'),
    one('rec_td', 'simple'),
    TGT_SHARE,
    FUM,
  ],
  WR: [...CATCHING, one('rush_yd', 'everything'), one('rush_td', 'everything'), FUM],
  TE: [...CATCHING, FUM],
  K: [
    {
      key: 'fg',
      label: 'FG',
      name: 'Field goals made, of those tried.',
      level: 'simple',
      figure: {
        made: ['fg_0_19', 'fg_20_29', 'fg_30_39', 'fg_40_49', 'fg_50p'],
        missed: ['fg_miss'],
      },
    },
    {
      key: 'xp',
      label: 'XP',
      name: 'Extra points made, of those tried.',
      level: 'simple',
      figure: { made: ['xp_made'], missed: ['xp_miss'] },
    },
    {
      key: 'fg_0_39',
      label: 'FG 0-39',
      name: 'Field goals made from under 40 yards.',
      level: 'standard',
      figure: { sum: ['fg_0_19', 'fg_20_29', 'fg_30_39'] },
    },
    one('fg_40_49', 'standard'),
    one('fg_50p', 'standard'),
    one('fg_miss', 'everything'),
    one('xp_miss', 'everything'),
  ],
  DST: [
    {
      key: 'dst_pa_game',
      label: 'PA/G',
      name: 'Points allowed a game.',
      level: 'simple',
      figure: { perGame: 'dst_pts_allowed' },
    },
    one('dst_sack', 'simple'),
    one('dst_int', 'simple'),
    one('dst_fum_rec', 'simple'),
    {
      key: 'dst_tds',
      label: 'TD',
      name: 'Touchdowns by the defense and on returns.',
      level: 'simple',
      figure: { sum: ['dst_td', 'dst_ret_td'] },
    },
    one('dst_safety', 'standard'),
    {
      key: 'dst_ya_game',
      label: 'YA/G',
      name: 'Yards allowed a game.',
      level: 'standard',
      figure: { perGame: 'dst_yds_allowed' },
    },
    one('dst_ff', 'everything'),
    one('dst_blk', 'everything'),
  ],
  DL: [
    TACKLES,
    one('idp_sack', 'simple'),
    one('idp_tkl_loss', 'simple'),
    ...SOLO_AST,
    one('idp_qb_hit', 'standard'),
    one('idp_ff', 'standard'),
    one('idp_fum_rec', 'everything'),
    one('idp_pass_def', 'everything'),
    one('idp_int', 'everything'),
    IDP_TD,
  ],
  LB: [
    TACKLES,
    one('idp_sack', 'simple'),
    one('idp_int', 'simple'),
    ...SOLO_AST,
    one('idp_tkl_loss', 'standard'),
    one('idp_pass_def', 'standard'),
    one('idp_ff', 'standard'),
    one('idp_qb_hit', 'everything'),
    one('idp_fum_rec', 'everything'),
    IDP_TD,
  ],
  DB: [
    TACKLES,
    one('idp_int', 'simple'),
    one('idp_pass_def', 'simple'),
    ...SOLO_AST,
    one('idp_ff', 'standard'),
    one('idp_sack', 'standard'),
    one('idp_tkl_loss', 'everything'),
    one('idp_qb_hit', 'everything'),
    one('idp_fum_rec', 'everything'),
    IDP_TD,
  ],
};

export const footballDisplay: DisplayRules = {
  summarizeLine(line: StatLine, positions: readonly PositionId[]): string {
    if (positions.includes('K')) return summarizeKicker(line);
    if (positions.includes('DST')) return summarizeDefense(line);
    // A two-way player reads by his offense; only a defender by his defense.
    if (positions.length > 0 && positions.every((p) => DEFENDERS.has(p)))
      return summarizeDefender(line);
    return summarizeOffense(line);
  },
  tableColumns(group, mode) {
    return (mode === 'pro' ? PRO : CASUAL)[group] ?? CASUAL.offense!;
  },
  gameLogColumns(group) {
    return GAME_LOG[group] ?? GAME_LOG.offense!;
  },
  statSheet(position) {
    return SHEETS[position] ?? null;
  },
  windowLabel(window) {
    // Football windows are whole weeks; the period label already says 'Week N'.
    const d = new Date(window.start);
    return `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })}`;
  },
};
